import type { FC } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { PitDetailsFromRetailSessionContextType } from './types/PitDetailsFromRetailSessionTypes';
import { persistedPrimeDecouplingPIT } from '@/clientState/storageState/persistedPrimeDecouplingPIT';

const defaultValue = {
  pitDetailsInRetailSession: undefined,
  setPitDetailsInRetailSession: () => {},
  resetPitDetailsInRetailSession: () => {},
};

export const PitDetailsFromRetailSessionContext = createContext<{
  pitDetailsInRetailSession: PitDetailsFromRetailSessionContextType | undefined;
  setPitDetailsInRetailSession: (
    _pitDetails: PitDetailsFromRetailSessionContextType | undefined
  ) => void;
  resetPitDetailsInRetailSession: () => void;
}>(defaultValue);

const userPersistedPrimeDecouplingPIT = persistedPrimeDecouplingPIT();

const PitDetailsFromRetailSession: FC = ({ children }) => {
  const [pitDetailsInSessionStorage, setPitDetailsInSessionStorage] =
    userPersistedPrimeDecouplingPIT();
  const [pitDetailsInMemory, setPitDetailsInMemory] =
    useState<PitDetailsFromRetailSessionContextType>(
      pitDetailsInSessionStorage
    );

  const setPitDetailsInRetailSession = useCallback(
    (pitDetails) => {
      setPitDetailsInSessionStorage(pitDetails);
      setPitDetailsInMemory(pitDetails);
    },
    [setPitDetailsInSessionStorage]
  );

  const resetPitDetailsInRetailSession = useCallback(() => {
    setPitDetailsInRetailSession(undefined);
  }, [setPitDetailsInRetailSession]);

  const value = useMemo(
    () => ({
      pitDetailsInRetailSession: pitDetailsInMemory,
      setPitDetailsInRetailSession,
      resetPitDetailsInRetailSession,
    }),
    [
      pitDetailsInMemory,
      setPitDetailsInRetailSession,
      resetPitDetailsInRetailSession,
    ]
  );

  return (
    <PitDetailsFromRetailSessionContext.Provider value={value}>
      {children}
    </PitDetailsFromRetailSessionContext.Provider>
  );
};

export default PitDetailsFromRetailSession;

export const usePitDetailsFromRetailSessionProvider = () => {
  return useContext(PitDetailsFromRetailSessionContext);
};
